import React, { useState, useEffect, useCallback } from 'react';
import { validateGSTNumber, formatGSTForDisplay, getGSTExamples, getStateFromGST, extractPANFromGST } from '../utils/gstVerification';

const GSTInput = ({
  value = '',
  onChange,
  onValidationChange,
  required = false,
  disabled = false,
  className = '',
  placeholder = 'Enter 15-digit GST number',
  autoFormat = false, // Disabled by default to avoid spacing issues
  showExamples = true,
  showStateInfo = true,
  showPANInfo = true,
  autoFetchDetails = false, // Made optional by default for non-GST users
  onCompanyDetailsFetch,
  apiProvider = 'mock'
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [validation, setValidation] = useState({
    isValid: null,
    message: '',
    stateInfo: null,
    panInfo: null
  });
  const [isValidating, setIsValidating] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Company details fetching state - REMOVED (GST API not implemented yet)

  // Debounced validation
  const [validationTimeout, setValidationTimeout] = useState(null);

  // Fetch company details from GST API - DISABLED (API not implemented yet)
  const fetchCompanyDetails = useCallback(async (validGST) => {
    // Auto-fetch functionality disabled - GST API not implemented yet
    return;
  }, []);

  // Validate GST number with debounce
  const validateGST = useCallback((gstValue) => {
    if (!gstValue || gstValue.length === 0) {
      setValidation({
        isValid: null,
        message: '',
        stateInfo: null,
        panInfo: null
      });
      onValidationChange?.({ isValid: null, gst: gstValue });
      return;
    }

    setIsValidating(true);

    // Clear previous timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    // Set new timeout for validation
    const timeout = setTimeout(async () => {
      const result = validateGSTNumber(gstValue);

      let stateInfo = null;
      let panInfo = null;

      if (result.isValid) {
        stateInfo = getStateFromGST(gstValue);
        panInfo = extractPANFromGST(gstValue);
      }

      const validationResult = {
        isValid: result.isValid,
        message: result.message,
        stateInfo,
        panInfo,
        cleanGST: result.cleanGST
      };

      setValidation(validationResult);
      setIsValidating(false);

      onValidationChange?.({
        isValid: result.isValid,
        gst: result.cleanGST || gstValue,
        stateInfo,
        panInfo,
        message: result.message
      });
    }, 500); // 500ms debounce

    setValidationTimeout(timeout);
  }, [validationTimeout, onValidationChange]);

  // Handle input change
  const handleInputChange = (e) => {
    let newValue = e.target.value;

    // Clean the input (remove spaces for processing)
    const cleanValue = newValue.replace(/\s/g, '').toUpperCase();

    // Limit to 15 characters
    if (cleanValue.length > 15) {
      return; // Don't allow more than 15 characters
    }

    // Auto-format while typing if enabled
    if (autoFormat && cleanValue.length > 0) {
      let formattedValue = cleanValue;

      // Add spaces at appropriate positions for display
      if (formattedValue.length > 2) {
        formattedValue = formattedValue.substring(0, 2) + ' ' + formattedValue.substring(2);
      }
      if (formattedValue.length > 8) {
        formattedValue = formattedValue.substring(0, 8) + ' ' + formattedValue.substring(8);
      }
      if (formattedValue.length > 13) {
        formattedValue = formattedValue.substring(0, 13) + ' ' + formattedValue.substring(13);
      }

      setInputValue(formattedValue);
    } else {
      setInputValue(cleanValue);
    }

    // Always pass clean value to parent and validation
    onChange?.(cleanValue);
    validateGST(cleanValue);
  };

  // Handle blur (when user leaves the input)
  const handleBlur = () => {
    if (inputValue && autoFormat) {
      const cleanValue = inputValue.replace(/\s/g, '').toUpperCase();
      if (cleanValue.length === 15) {
        const formatted = formatGSTForDisplay(cleanValue);
        setInputValue(formatted);
      }
    }
  };

  // Handle focus (when user enters the input)
  const handleFocus = () => {
    if (inputValue && autoFormat) {
      // Remove formatting for easier editing
      const cleanValue = inputValue.replace(/\s/g, '');
      setInputValue(cleanValue);
    }
  };

  // Handle key down to prevent unwanted character insertion
  const handleKeyDown = (e) => {
    const cleanValue = inputValue.replace(/\s/g, '');

    // Prevent typing when we already have 15 characters
    if (cleanValue.length >= 15 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
      e.preventDefault();
      return;
    }

    // Allow navigation keys
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      return;
    }

    // Allow control keys
    if (e.ctrlKey || e.metaKey) {
      return;
    }
  };


  // Handle toggle auto-fetch
  const handleToggleAutoFetch = () => {
    // This would be passed from parent component to control auto-fetch globally
    console.log('Toggle auto-fetch clicked');
  };

  // Update input value when prop changes
  useEffect(() => {
    if (value !== inputValue.replace(/\s/g, '').toUpperCase()) {
      setInputValue(value);
      validateGST(value);
    }
  }, [value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  // Get input class based on validation state
  const getInputClassName = () => {
    const baseClass = `mt-1 block w-full border rounded-md shadow-sm p-3 text-base transition-colors duration-200 ${className}`;

    if (validation.isValid === null) {
      return `${baseClass} border-gray-300 focus:ring-blue-500 focus:border-blue-500`;
    }

    if (validation.isValid === true) {
      return `${baseClass} border-green-500 focus:ring-green-500 focus:border-green-500 bg-green-50`;
    }

    if (validation.isValid === false) {
      return `${baseClass} border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50`;
    }

    return baseClass;
  };

  // Get validation icon
  const getValidationIcon = () => {
    if (isValidating) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      );
    }

    if (validation.isValid === true) {
      return (
        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }

    if (validation.isValid === false) {
      return (
        <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }

    return null;
  };

  const gstExamples = getGSTExamples();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          GSTIN {required && <span className="text-red-500">*</span>}
        </label>
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {showHelp ? 'Hide Help' : 'Show Help'}
        </button>
      </div>

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={getInputClassName()}
          placeholder={placeholder}
          maxLength={autoFormat ? 17 : 15} // Account for spaces in formatted version
          required={required}
        />

        {/* Validation Icon */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {getValidationIcon()}
        </div>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">GST Number Format:</p>
            <p className="mb-2"><strong>22AAAAA0000A1Z5</strong> (15 digits, no spaces)</p>
            <ul className="text-xs space-y-1 mb-3">
              <li>• First 2 digits: State code (e.g., 27 for Maharashtra)</li>
              <li>• Next 10 characters: PAN number of the entity</li>
              <li>• 13th character: Entity number (1-9, A-Z)</li>
              <li>• 14th character: 'Z' (always)</li>
              <li>• 15th character: Checksum digit</li>
            </ul>
            <div className="bg-green-50 border border-green-200 rounded p-2 mb-3">
              <p className="text-xs font-medium text-green-800 mb-1">✅ Typing Tips:</p>
              <ul className="text-xs text-green-700 space-y-1">
                <li>• Type all 15 digits continuously (no spaces needed)</li>
                <li>• Auto-formatting is disabled to avoid typing issues</li>
                <li>• You can copy-paste complete GST numbers</li>
              </ul>
            </div>


            {/* API Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-3">
              <p className="text-xs font-medium text-blue-800 mb-1">📡 GST API Integration:</p>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Current Provider:</strong> {apiProvider === 'gstn' ? 'GSTN Official API' : apiProvider === 'cleartax' ? 'ClearTax API' : apiProvider === 'gstzen' ? 'GSTZen API' : 'Mock Data (Demo)'}</p>

                {apiProvider === 'gstn' && (
                  <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                    <p className="text-xs font-medium text-green-800">✅ Official GSTN API:</p>
                    <ul className="text-xs text-green-700 mt-1">
                      <li>• Direct integration with GST portal</li>
                      <li>• Real-time company verification</li>
                      <li>• Official government data</li>
                      <li>• Requires GSP certification</li>
                    </ul>
                  </div>
                )}

                {apiProvider === 'mock' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                    <p className="text-xs font-medium text-yellow-800">⚠️ Demo Mode:</p>
                    <p className="text-xs text-yellow-700">Using sample data for testing. Configure real API for production use.</p>
                  </div>
                )}

                {apiProvider !== 'gstn' && apiProvider !== 'mock' && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-2 mt-2">
                    <p className="text-xs font-medium text-orange-800">🔄 Third-party API:</p>
                    <ul className="text-xs text-orange-700 mt-1">
                      <li>• Fast and reliable service</li>
                      <li>• Easy setup and integration</li>
                      <li>• Additional business features</li>
                      <li>• Paid service with usage limits</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* GSTN API Setup Guide */}
            {apiProvider === 'gstn' && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                <p className="text-xs font-medium text-green-800 mb-2">🚀 GSTN API Setup Required:</p>
                <div className="text-xs text-green-700 space-y-1">
                  <p><strong>1. Register as GSP:</strong> Visit <a href="https://developer.gst.gov.in" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">GST Developer Portal</a></p>
                  <p><strong>2. Get API Credentials:</strong> Complete GSTN certification process</p>
                  <p><strong>3. Configure Environment:</strong> Add credentials to .env file</p>
                  <p><strong>4. Test Integration:</strong> Use sandbox environment first</p>
                  <div className="mt-2 p-2 bg-white rounded border">
                    <p className="font-medium text-xs mb-1">Required .env Variables:</p>
                    <code className="text-xs">
                      REACT_APP_GSTN_CLIENT_ID=<br/>
                      REACT_APP_GSTN_CLIENT_SECRET=<br/>
                      REACT_APP_GSTN_USERNAME=<br/>
                      REACT_APP_GSTN_PASSWORD=
                    </code>
                  </div>
                </div>
              </div>
            )}

            {showExamples && (
              <>
                <p className="font-medium mb-2">Examples:</p>
                <div className="space-y-1">
                  {gstExamples.map((example, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setInputValue(example.gst);
                        onChange?.(example.gst);
                        validateGST(example.gst);
                        setShowHelp(false);
                      }}
                      className="block text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100 px-2 py-1 rounded w-full text-left"
                    >
                      <strong>{example.state}:</strong> {example.gst}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Validation Message */}
      {validation.message && (
        <div className={`text-sm p-2 rounded-md ${
          validation.isValid === true
            ? 'bg-green-50 text-green-800 border border-green-200'
            : validation.isValid === false
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {validation.message}
        </div>
      )}

      {/* State Information */}
      {showStateInfo && validation.stateInfo && validation.isValid && (
        <div className="bg-green-50 border border-green-200 rounded-md p-2">
          <div className="text-sm text-green-800">
            <strong>📍 State:</strong> {validation.stateInfo.name} ({validation.stateInfo.code})
          </div>
        </div>
      )}

      {/* PAN Information */}
      {showPANInfo && validation.panInfo && validation.isValid && (
        <div className="bg-green-50 border border-green-200 rounded-md p-2">
          <div className="text-sm text-green-800">
            <strong>🆔 PAN:</strong> {validation.panInfo}
          </div>
        </div>
      )}

      {/* Loading Indicators */}
      {isValidating && (
        <div className="text-sm text-blue-600 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          Verifying GST number...
        </div>
      )}

    </div>
  );
};

export default GSTInput;
